import React, { useState, useEffect } from 'react';
import { Container, Card, Button, Alert, Table, Form, Modal, Badge } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { countBimbinganSessions, calculateSeminarScore } from '../utils/seminarUtils';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  getDoc, 
  addDoc, 
  updateDoc,
  orderBy,
  deleteDoc
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { db, storage } from '../firebase/config';
import { FaUpload, FaDownload, FaEye, FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import { v4 as uuidv4 } from 'uuid';
import EditBimbinganModal from '../components/bimbingan/EditBimbinganModal';

const Bimbingan = () => {
  const { currentUser, userRole } = useAuth();
  const [tugasAkhir, setTugasAkhir] = useState(null);
  const [bimbinganList, setBimbinganList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [showAddModal, setShowAddModal] = useState(false);
  
  // Fungsi untuk membuka modal tambah bimbingan dan pre-fill dengan link terakhir jika ada
  const handleShowAddModal = async () => {
    // Cek apakah sudah ada sesi bimbingan sebelumnya
    if (bimbinganList && bimbinganList.length > 0) {
      // Ambil link bimbingan terakhir (dari sesi terbaru)
      const sortedBimbingan = [...bimbinganList].sort((a, b) => (b.sesi || 0) - (a.sesi || 0));
      if (sortedBimbingan[0] && sortedBimbingan[0].fileURL) {
        setCatatan(sortedBimbingan[0].fileURL);
      }
    }
    
    // Tentukan jenis bimbingan berdasarkan kemajuan
    // Filter bimbingan yang sudah diulas (reviewed)
    const reviewedBimbingan = bimbinganList.filter(b => b.status === 'reviewed');
    
    // Hitung jumlah proposal, hasil, dan finalisasi
    const proposalCount = reviewedBimbingan.filter(b => b.jenisBimbingan === 'proposal' || !b.jenisBimbingan).length;
    const hasilCount = reviewedBimbingan.filter(b => b.jenisBimbingan === 'hasil').length;
    const finalisasiCount = reviewedBimbingan.filter(b => b.jenisBimbingan === 'finalisasi').length;
    
    // Check if seminar proposal has been completed and if passed
    try {
      let proposalCompleted = false;
      let proposalPassed = false;
      
      // Query untuk cek seminar proposal - coba dengan orderBy dulu, jika gagal pakai tanpa orderBy
      let seminarProposalSnapshot = null;
      try {
        const seminarProposalQuery = query(
          collection(db, "seminar"),
          where("tugasAkhirId", "==", tugasAkhir.id),
          where("jenis", "==", "proposal"),
          orderBy("completedAt", "desc")
        );
        seminarProposalSnapshot = await getDocs(seminarProposalQuery);
      } catch (orderByError) {
        // Jika orderBy gagal (misalnya tidak ada index), coba tanpa orderBy
        console.log("OrderBy failed, trying without orderBy:", orderByError);
        const seminarProposalQueryWithoutOrder = query(
          collection(db, "seminar"),
          where("tugasAkhirId", "==", tugasAkhir.id),
          where("jenis", "==", "proposal")
        );
        seminarProposalSnapshot = await getDocs(seminarProposalQueryWithoutOrder);
      }
      
      // Check if proposal is completed and if passed (score >= 60)
      if (seminarProposalSnapshot && !seminarProposalSnapshot.empty) {
        // Sort by completedAt manually if available, or by tanggal
        let seminarDocs = [...seminarProposalSnapshot.docs];
        seminarDocs.sort((a, b) => {
          const aData = a.data();
          const bData = b.data();
          const aDate = aData.completedAt || aData.tanggal || '';
          const bDate = bData.completedAt || bData.tanggal || '';
          return bDate.localeCompare(aDate);
        });
        
        // Find the first completed seminar (not superseded or cancelled)
        let proposalData = null;
        for (const doc of seminarDocs) {
          const data = doc.data();
          if (data.status === 'completed' && !data.superseded && !data.cancelled) {
            proposalData = data;
            break;
          }
        }
        
        if (!proposalData && seminarDocs.length > 0) {
          // Fallback: use first document if no non-superseded found
          proposalData = seminarDocs[0].data();
        }
        
        if (proposalData) {
          proposalCompleted = proposalData.status === 'completed';
          
          // Calculate if student passed (score >= 60)
          if (proposalCompleted && proposalData.nilai) {
            // Get all penguji scores
            const pengujiScores = Object.values(proposalData.nilai);
            if (pengujiScores.length === 4) {
              // Use the standard utility function to calculate score
              const scoreResult = calculateSeminarScore(proposalData);
              const totalScore = scoreResult.finalScore;
              
              // Student passes if score >= 60
              proposalPassed = scoreResult.isPassed;
              console.log(`Proposal final score: ${totalScore}, passed: ${proposalPassed}`);
            } else {
              console.log(`Proposal not yet fully graded: ${pengujiScores.length}/4 penguji`);
            }
          }
        }
      }
      
      console.log(`Seminar proposal status: completed=${proposalCompleted}, passed=${proposalPassed}`);
      
      // Save proposal seminar status to state
      setProposalSeminarCompleted(proposalCompleted);
      setProposalSeminarPassed(proposalPassed);
      
      // Query untuk cek seminar hasil - coba dengan orderBy dulu, jika gagal pakai tanpa orderBy
      let hasilSnapshot = null;
      let hasilCompleted = false;
      let hasilPassed = false;
      
      try {
        const hasilQuery = query(
          collection(db, "seminar"),
          where("tugasAkhirId", "==", tugasAkhir.id),
          where("jenis", "==", "hasil"),
          orderBy("completedAt", "desc")
        );
        hasilSnapshot = await getDocs(hasilQuery);
      } catch (orderByError) {
        // Jika orderBy gagal (misalnya tidak ada index), coba tanpa orderBy
        console.log("OrderBy failed for hasil, trying without orderBy:", orderByError);
        const hasilQueryWithoutOrder = query(
          collection(db, "seminar"),
          where("tugasAkhirId", "==", tugasAkhir.id),
          where("jenis", "==", "hasil")
        );
        hasilSnapshot = await getDocs(hasilQueryWithoutOrder);
      }
      
      // Check if hasil is completed and if passed (score >= 60)
      if (hasilSnapshot && !hasilSnapshot.empty) {
        // Sort by completedAt manually if available, or by tanggal
        let hasilDocs = [...hasilSnapshot.docs];
        hasilDocs.sort((a, b) => {
          const aData = a.data();
          const bData = b.data();
          const aDate = aData.completedAt || aData.tanggal || '';
          const bDate = bData.completedAt || bData.tanggal || '';
          return bDate.localeCompare(aDate);
        });
        
        // Find the first completed seminar (not superseded or cancelled)
        let hasilData = null;
        for (const doc of hasilDocs) {
          const data = doc.data();
          if (data.status === 'completed' && !data.superseded && !data.cancelled) {
            hasilData = data;
            break;
          }
        }
        
        if (!hasilData && hasilDocs.length > 0) {
          // Fallback: use first document if no non-superseded found
          hasilData = hasilDocs[0].data();
        }
        
        if (hasilData) {
          hasilCompleted = hasilData.status === 'completed';
          
          // Calculate if student passed (score >= 60)
          if (hasilCompleted && hasilData.nilai) {
            // Get all penguji scores
            const pengujiScores = Object.values(hasilData.nilai);
            if (pengujiScores.length === 4) {
              // Use the standard utility function to calculate score
              const scoreResult = calculateSeminarScore(hasilData);
              const totalScore = scoreResult.finalScore;
              
              // Student passes if score >= 60
              hasilPassed = scoreResult.isPassed;
              console.log(`Hasil final score: ${totalScore}, passed: ${hasilPassed}`);
            } else {
              console.log(`Hasil not yet fully graded: ${pengujiScores.length}/4 penguji`);
            }
          }
        }
      }
      
      console.log(`Seminar hasil status: completed=${hasilCompleted}, passed=${hasilPassed}`);
      
      // Save hasil seminar status to state
      setHasilSeminarCompleted(hasilCompleted);
      setHasilSeminarPassed(hasilPassed);
      
      // Set jenis bimbingan default berdasarkan status seminar dan hasil ujian
      // Default akan mengarah ke jenis bimbingan yang paling sesuai, tapi user bisa ubah
      if (hasilCompleted && hasilPassed) {
        // Jika seminar hasil sudah selesai dan lulus, default ke finalisasi (tapi bisa pilih semua)
        setJenisBimbingan('finalisasi');
      } else if (proposalCompleted && proposalPassed) {
        // Jika seminar proposal sudah selesai dan lulus, default ke hasil (tapi bisa pilih proposal atau hasil)
        setJenisBimbingan('hasil');
      } else {
        // Jika belum ada seminar yang selesai/lulus, tetap di proposal
        setJenisBimbingan('proposal');
      }
      
      console.log(`Setting jenis bimbingan based on: proposalCompleted=${proposalCompleted}, proposalPassed=${proposalPassed}, hasilCompleted=${hasilCompleted}, hasilPassed=${hasilPassed}`);
      
      // Open modal after status is checked and state is updated
      setShowAddModal(true);
    } catch (error) {
      console.error("Error checking seminar status:", error);
      // Default to proposal if there's an error
      setJenisBimbingan('proposal');
      setProposalSeminarCompleted(false);
      setProposalSeminarPassed(false);
      setHasilSeminarCompleted(false);
      setHasilSeminarPassed(false);
      // Still open modal even if there's an error
      setShowAddModal(true);
    }
  };
  
  // Fungsi untuk reset form tambah bimbingan
  const resetAddBimbinganForm = () => {
    setCatatan('');
    setSelectedPembimbing('');
    setTanggalBimbingan('');
    setCatatanDospem('');
    setTanggalRevisi('');
    setJenisBimbingan('proposal');
    setProposalSeminarCompleted(false);
    setProposalSeminarPassed(false);
    setHasilSeminarCompleted(false);
    setHasilSeminarPassed(false);
  };
  
  // Fungsi untuk mengecek dosen mana yang memiliki sesi bimbingan pending
  const getDosensWithPendingSessions = () => {
    // Ambil daftar dosen yang memiliki sesi bimbingan pending
    const pendingDosenIds = [];
    
    bimbinganList.forEach(bimbingan => {
      // CRITICAL: Only count non-deleted pending sessions
      if (bimbingan.status === 'pending' && bimbingan.deleted !== true) {
        // Jika pembimbing belum ada di array, tambahkan
        if (!pendingDosenIds.includes(bimbingan.pembimbingId)) {
          pendingDosenIds.push(bimbingan.pembimbingId);
        }
      }
    });
    
    console.log("Dosen dengan sesi pending:", pendingDosenIds);
    return pendingDosenIds;
  };
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false); // Still needed for backward compatibility
  const [selectedBimbingan, setSelectedBimbingan] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEditBimbingan, setSelectedEditBimbingan] = useState(null);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [errorEdit, setErrorEdit] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [catatan, setCatatan] = useState('');
  const [selectedPembimbing, setSelectedPembimbing] = useState('');
  const [tanggalBimbingan, setTanggalBimbingan] = useState('');
  const [catatanDospem, setCatatanDospem] = useState('');
  const [tanggalRevisi, setTanggalRevisi] = useState('');

  useEffect(() => {
    // Check if there's a specific mahasiswa filter from MahasiswaBimbingan page
    const mahasiswaId = localStorage.getItem('currentBimbinganMahasiswaId');
    const taId = localStorage.getItem('currentBimbinganTugasAkhirId');
    
    fetchData(mahasiswaId, taId);
    
    // Clear localStorage after reading
    localStorage.removeItem('currentBimbinganMahasiswaId');
    localStorage.removeItem('currentBimbinganTugasAkhirId');
  }, [currentUser, userRole]);

  const fetchData = async (filterMahasiswaId = null, filterTaId = null) => {
    try {
      setLoading(true);
      console.log("Fetching bimbingan data with filters:", { filterMahasiswaId, filterTaId });
      
      if (!currentUser) return;
      
      // Ambil data tugas akhir berdasarkan peran
      if (userRole === 'mahasiswa') {
        // Mahasiswa: ambil tugas akhir berdasarkan mahasiswaId
        const taQuery = query(
          collection(db, "tugasAkhir"),
          where("mahasiswaId", "==", currentUser.uid)
        );
        const taSnapshot = await getDocs(taQuery);
        
        if (!taSnapshot.empty) {
          const taData = taSnapshot.docs[0].data();
          taData.id = taSnapshot.docs[0].id;
          
          // Ambil data pembimbing (support array)
          if (taData.pembimbingId) {
            if (Array.isArray(taData.pembimbingId)) {
              taData.pembimbing = [];
              for (const pembimbingId of taData.pembimbingId) {
                const pembimbingDoc = await getDoc(doc(db, "users", pembimbingId));
                if (pembimbingDoc.exists()) {
                  taData.pembimbing.push({ id: pembimbingId, ...pembimbingDoc.data() });
                }
              }
            } else {
              // fallback jika masih single value
              const pembimbingDoc = await getDoc(doc(db, "users", taData.pembimbingId));
              if (pembimbingDoc.exists()) {
                taData.pembimbing = [{ id: taData.pembimbingId, ...pembimbingDoc.data() }];
              }
            }
          }
          
          setTugasAkhir(taData);
          
          // Ambil data bimbingan
          if (taData.status === 'approved') {
            // We'll filter deleted bimbingan in memory
            const bimbinganQuery = query(
              collection(db, "bimbingan"),
              where("tugasAkhirId", "==", taData.id),
              orderBy("tanggal", "desc")
            );
            const bimbinganSnapshot = await getDocs(bimbinganQuery);
            
            const bimbinganData = [];
            console.log(`Bimbingan.js: Processing ${bimbinganSnapshot.size} documents for TA ${taData.id}`);
            
            // Create a Set to track unique bimbingan IDs
            const uniqueIds = new Set();
            
            bimbinganSnapshot.forEach(doc => {
              const data = doc.data();
              console.log(`Bimbingan.js: Bimbingan ${doc.id}:`, data);
              
              // CRITICAL CHECK: Only include non-deleted bimbingan and not marked as pembimbing lama
              // Make sure we're checking with strict equality to catch all forms of deleted flag
              if (data.deleted === true) {
                console.log(`Bimbingan.js: Skipping deleted bimbingan ${doc.id}`);
              } else if (data.pembimbingLama === true) {
                console.log(`Bimbingan.js: Skipping bimbingan ${doc.id} with pembimbing lama flag`);
              } else {
                // Check for duplicates by ID
                if (uniqueIds.has(doc.id)) {
                  console.log(`Bimbingan.js: Skipping duplicate bimbingan ${doc.id}`);
                } else {
                  uniqueIds.add(doc.id);
                  // Double-verify that the document has a valid pembimbingId
                  if (!data.pembimbingId) {
                    console.log(`Bimbingan.js: Skipping bimbingan ${doc.id} with missing pembimbingId`);
                  } else {
                    // Include key metadata in logs for troubleshooting
                    console.log(`Bimbingan.js: Adding to list - ${doc.id}, pembimbing=${data.pembimbingId}, jenis=${data.jenisBimbingan}, status=${data.status}`);
                    bimbinganData.push({
                      id: doc.id,
                      ...data
                    });
                  }
                }
              }
            });
            
            console.log(`Bimbingan.js: After filtering, ${bimbinganData.length} bimbingan remain`);
            
            // Log each bimbingan for debugging
            bimbinganData.forEach((b, index) => {
              console.log(`Bimbingan.js: Item ${index + 1}: id=${b.id}, status=${b.status}, type=${b.jenisBimbingan || 'proposal'}`);
            });
            
            setBimbinganList(bimbinganData);
          }
        }
      } else if (userRole === 'dosen') {
        // Dosen: ambil tugas akhir yang dosen menjadi pembimbing
        console.log("Mencari TA dengan dosen ID:", currentUser.uid);
        
        // Query tugas akhir 
        let taSnapshot;
        try {
          let queryRef;
          
          if (filterTaId) {
            // If we have a specific tugas akhir to filter
            console.log(`Filtering by specific TA ID: ${filterTaId}`);
            queryRef = query(
              collection(db, "tugasAkhir"),
              where("__name__", "==", filterTaId)
            );
          } else {
            // Ambil semua tugas akhir terlebih dahulu (tanpa filter)
            queryRef = query(collection(db, "tugasAkhir"));
          }
          
          taSnapshot = await getDocs(queryRef);
          console.log("Total tugas akhir yang ditemukan:", taSnapshot.size);
          
          // Buat array untuk menyimpan dokumen yang cocok
          const filteredDocs = [];
          
          // Periksa setiap dokumen secara manual
          taSnapshot.forEach(doc => {
            const data = doc.data();
            
            // Debug log untuk melihat format data pembimbingId
            console.log(`Dokumen ${doc.id} - pembimbingId:`, data.pembimbingId, 
              `tipe: ${typeof data.pembimbingId}`,
              `isArray: ${Array.isArray(data.pembimbingId)}`);
            
            // Cek apakah dosen menjadi pembimbing di tugas akhir ini
            let isPembimbing = false;
            
            if (Array.isArray(data.pembimbingId)) {
              // Jika berbentuk array, cek apakah array mengandung ID dosen
              isPembimbing = data.pembimbingId.includes(currentUser.uid);
            } else if (typeof data.pembimbingId === 'string') {
              // Jika berbentuk string, cek apakah sama dengan ID dosen
              isPembimbing = data.pembimbingId === currentUser.uid;
            }
            
            // Jika dosen adalah pembimbing, tambahkan dokumen ke hasil filter
            if (isPembimbing) {
              console.log(`Dokumen ${doc.id} match untuk dosen ${currentUser.uid}`);
              filteredDocs.push({
                id: doc.id,
                ...data
              });
            }
          });
          
          console.log("Jumlah TA setelah filter manual:", filteredDocs.length);
          
          // Overwrite taSnapshot.docs dengan hasil filter
          taSnapshot = {
            docs: filteredDocs.map(doc => ({
              id: doc.id,
              data: () => doc
            })),
            size: filteredDocs.length,
            empty: filteredDocs.length === 0,
            forEach: (callback) => filteredDocs.forEach(doc => 
              callback({id: doc.id, data: () => doc, exists: () => true})
            )
          };
          
        } catch (error) {
          console.error("Error querying tugas akhir:", error);
          // Fallback ke array kosong jika ada error
          taSnapshot = {
            docs: [],
            size: 0,
            empty: true,
            forEach: () => {}
          };
        }
        
        // Ambil bimbingan untuk semua tugas akhir yang dibimbing
        const allBimbingan = [];
        
        // Juga coba ambil langsung dari bimbingan
        try {
          console.log("Mencari bimbingan langsung untuk dosen:", currentUser.uid);
          
          // Get all bimbingan documents (we'll filter in memory)
          // Use a simpler query to avoid index issues
          const directBimbinganQuery = query(collection(db, "bimbingan"));
          const directBimbinganSnapshot = await getDocs(directBimbinganQuery);
          console.log("Total bimbingan documents:", directBimbinganSnapshot.size);
          
          // Count matches
          let matchCount = 0;
          
          // For each bimbingan
          for (const bimbinganDoc of directBimbinganSnapshot.docs) {
            const bimbinganData = bimbinganDoc.data();
            
            // If it's for the current user and not deleted and not marked as pembimbing lama
            // CRITICAL CHECK: Use strict equality comparison for the deleted flag
            if (bimbinganData.pembimbingId === currentUser.uid && 
                bimbinganData.deleted !== true &&
                bimbinganData.pembimbingLama !== true) {
              matchCount++;
              
              // Skip if we're filtering by mahasiswaId and it doesn't match
              if (filterMahasiswaId && bimbinganData.mahasiswaId !== filterMahasiswaId) {
                continue;
              }
              
              // If it has a tugasAkhirId, get the TA data
              if (bimbinganData.tugasAkhirId) {
                const taData = { id: bimbinganData.tugasAkhirId };
                
                try {
                  const taDoc = await getDoc(doc(db, "tugasAkhir", bimbinganData.tugasAkhirId));
                  if (taDoc.exists()) {
                    Object.assign(taData, taDoc.data());
                  }
                } catch (error) {
                  console.error("Error fetching TA data:", error);
                }
                
                // Get mahasiswa data if available
                if (bimbinganData.mahasiswaId) {
                  try {
                    const mahasiswaDoc = await getDoc(doc(db, "users", bimbinganData.mahasiswaId));
                    if (mahasiswaDoc.exists()) {
                      taData.mahasiswa = mahasiswaDoc.data();
                    }
                  } catch (error) {
                    console.error("Error fetching mahasiswa data:", error);
                  }
                }
                
                // Add to the results
                allBimbingan.push({
                  id: bimbinganDoc.id,
                  ...bimbinganData,
                  tugasAkhir: taData
                });
              }
            }
          }
          
          console.log(`Found ${matchCount} bimbingan records matching dosen, added ${allBimbingan.length} to results`);
        } catch (error) {
          console.error("Error querying bimbingan directly:", error);
        }
        
        // Lanjutkan dengan cara original untuk redundansi
        for (const taDoc of taSnapshot.docs) {
          const taData = {
            id: taDoc.id,
            ...taDoc.data()
          };
          
          // Ambil data mahasiswa
          if (taData.mahasiswaId) {
            const mahasiswaDoc = await getDoc(doc(db, "users", taData.mahasiswaId));
            if (mahasiswaDoc.exists()) {
              taData.mahasiswa = mahasiswaDoc.data();
            }
          }
          
          // Ambil data bimbingan
          console.log(`Mencari bimbingan untuk TA ID: ${taData.id}`);
          let bimbinganQuery;
          
          // Let's simplify the query to avoid requiring complex indexes
          if (filterMahasiswaId) {
            // If filtering by specific mahasiswa
            console.log(`Also filtering bimbingan by mahasiswa ID: ${filterMahasiswaId}`);
            // First get all by tugasAkhirId
            bimbinganQuery = query(
              collection(db, "bimbingan"),
              where("tugasAkhirId", "==", taData.id)
            );
          } else {
            // Normal bimbingan query - just get by tugasAkhirId
            bimbinganQuery = query(
              collection(db, "bimbingan"),
              where("tugasAkhirId", "==", taData.id)
            );
          }
          
          const bimbinganSnapshot = await getDocs(bimbinganQuery);
          console.log(`Bimbingan ditemukan: ${bimbinganSnapshot.size} untuk TA ID: ${taData.id}`);
          
          // Filter and sort in memory rather than in the query
          bimbinganSnapshot.forEach(bimbinganDoc => {
            const bimbinganData = bimbinganDoc.data();
            
            // Only push the bimbingan if it matches our filter criteria and is not deleted
            // CRITICAL CHECK: Use strict equality comparison for deleted flag
            if ((bimbinganData.pembimbingId === currentUser.uid) && 
                (!filterMahasiswaId || bimbinganData.mahasiswaId === filterMahasiswaId) &&
                (bimbinganData.deleted !== true)) {
              
              allBimbingan.push({
                id: bimbinganDoc.id,
                ...bimbinganData,
                tugasAkhir: taData
              });
            }
          });
          
          // Sort by tanggal desc in memory since we're not using orderBy in the query
          allBimbingan.sort((a, b) => {
            // If either tanggal is missing, put it at the end
            if (!a.tanggal) return 1;
            if (!b.tanggal) return -1;
            
            // Sort newest first
            return new Date(b.tanggal) - new Date(a.tanggal);
          });
        }
        
        setBimbinganList(allBimbingan);
      } else if (userRole === 'pj') {
        // Admin (PJ): Ambil semua bimbingan
        console.log("Fetching all bimbingan data for admin");
        const bimbinganQuery = query(
          collection(db, "bimbingan"),
          orderBy("tanggal", "desc") // Urutkan berdasarkan tanggal terbaru
        );
        const bimbinganSnapshot = await getDocs(bimbinganQuery);
        
        const allBimbinganAdmin = [];
        const userCache = {}; // Cache untuk data user (mahasiswa/dosen)
        
        for (const bimbinganDoc of bimbinganSnapshot.docs) {
          const bimbinganData = bimbinganDoc.data();
          
          // Skip deleted bimbingan
          if (bimbinganData.deleted === true) continue;
          
          const bimbinganItem = {
            id: bimbinganDoc.id,
            ...bimbinganData,
            mahasiswa: null,
            dosen: null
          };
          
          // Ambil data mahasiswa (dari cache atau Firestore)
          if (bimbinganData.mahasiswaId) {
            if (!userCache[bimbinganData.mahasiswaId]) {
              try {
                const userDoc = await getDoc(doc(db, "users", bimbinganData.mahasiswaId));
                if (userDoc.exists()) {
                  userCache[bimbinganData.mahasiswaId] = userDoc.data();
                }
              } catch (err) { console.error("Error fetching mahasiswa:", err); }
            }
            bimbinganItem.mahasiswa = userCache[bimbinganData.mahasiswaId];
          }
          
          // Ambil data dosen (dari cache atau Firestore)
          if (bimbinganData.pembimbingId) {
            if (!userCache[bimbinganData.pembimbingId]) {
              try {
                const userDoc = await getDoc(doc(db, "users", bimbinganData.pembimbingId));
                if (userDoc.exists()) {
                  userCache[bimbinganData.pembimbingId] = userDoc.data();
                }
              } catch (err) { console.error("Error fetching dosen:", err); }
            }
            bimbinganItem.dosen = userCache[bimbinganData.pembimbingId];
          }
          
          allBimbinganAdmin.push(bimbinganItem);
        }
        
        console.log(`Admin fetched ${allBimbinganAdmin.length} bimbingan records`);
        setBimbinganList(allBimbinganAdmin);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Terjadi kesalahan saat mengambil data: " + error.message); // Include error message
    } finally {
      setLoading(false);
    }
  };

  const handleLinkChange = (e) => {
    setCatatan(e.target.value);
  };
  
  // Fungsi untuk mendapatkan link terakhir dari dosen pembimbing
  const getLastLinkForPembimbing = (pembimbingId) => {
    if (!pembimbingId || !bimbinganList || bimbinganList.length === 0) return '';
    
    // Filter bimbingan untuk dosen yang dipilih dan urutkan berdasarkan sesi (terbaru terlebih dahulu)
    const dosenBimbingan = bimbinganList
      .filter(b => b.pembimbingId === pembimbingId)
      .sort((a, b) => (b.sesi || 0) - (a.sesi || 0));
    
    // Ambil link dari sesi terakhir jika ada
    if (dosenBimbingan.length > 0) {
      return dosenBimbingan[0].fileURL || '';
    }
    
    return '';
  };

  const validateDriveLink = (link) => {
    // Validasi Google Drive link (seperti proposal)
    return (
      typeof link === 'string' &&
      link.startsWith('https://drive.google.com/') &&
      (link.includes('/file/d/') || link.includes('/folders/') || link.includes('id='))
    );
  };
  
  // Fungsi untuk debugging dan perbaikan data bimbingan (untuk admin)
  // Fungsi ini hanya untuk penggunaan development
  const fixBimbinganData = async () => {
    try {
      console.log("Mencoba memperbaiki data bimbingan...");
      
      // Ambil semua data bimbingan
      const bimbinganSnapshot = await getDocs(collection(db, "bimbingan"));
      console.log(`Total data bimbingan: ${bimbinganSnapshot.size}`);
      
      for (const doc of bimbinganSnapshot.docs) {
        const data = doc.data();
        console.log(`Bimbingan ${doc.id}:`, data);
        
        // Cek apakah pembimbingId ada
        if (!data.pembimbingId) {
          console.log(`Bimbingan ${doc.id} tidak memiliki pembimbingId`);
          
          // Coba ambil dari tugas akhir
          if (data.tugasAkhirId) {
            try {
              const taDoc = await getDoc(doc(db, "tugasAkhir", data.tugasAkhirId));
              if (taDoc.exists()) {
                const taData = taDoc.data();
                console.log(`Data TA untuk bimbingan ${doc.id}:`, taData);
                
                // Cek apakah pembimbingId ada dalam TA
                if (taData.pembimbingId) {
                  let pembimbingId;
                  if (Array.isArray(taData.pembimbingId)) {
                    pembimbingId = taData.pembimbingId[0]; // Ambil yang pertama
                  } else {
                    pembimbingId = taData.pembimbingId;
                  }
                  
                  // Update data bimbingan
                  console.log(`Memperbaiki bimbingan ${doc.id} dengan pembimbingId: ${pembimbingId}`);
                  await updateDoc(doc.ref, { pembimbingId });
                }
              }
            } catch (error) {
              console.error(`Error memperbaiki bimbingan ${doc.id}:`, error);
            }
          }
        }
      }
      
      console.log("Perbaikan data bimbingan selesai");
      return { success: true, message: "Perbaikan data selesai" };
    } catch (error) {
      console.error("Error memperbaiki data:", error);
      return { success: false, error: error.message };
    }
  };

  const [jenisBimbingan, setJenisBimbingan] = useState('proposal');
  const [proposalSeminarCompleted, setProposalSeminarCompleted] = useState(false);
  const [proposalSeminarPassed, setProposalSeminarPassed] = useState(false);
  const [hasilSeminarCompleted, setHasilSeminarCompleted] = useState(false);
  const [hasilSeminarPassed, setHasilSeminarPassed] = useState(false);

  const handleAddBimbingan = async () => {
    try {
      console.log("Menambahkan bimbingan baru", {
        catatan,
        selectedPembimbing,
        tanggalBimbingan,
        tanggalRevisi,
        tugasAkhir,
        jenisBimbingan
      });
      
      if (!catatan || !validateDriveLink(catatan)) {
        setError("Masukkan link Google Drive dokumen bimbingan yang valid!");
        return;
      }
      if (!selectedPembimbing) {
        setError("Pilih dosen pembimbing terlebih dahulu!");
        return;
      }
      if (!tanggalBimbingan) {
        setError("Pilih tanggal bimbingan!");
        return;
      }
      if (!tanggalRevisi) {
        setError("Pilih tanggal maksimal revisi!");
        return;
      }
      if (!jenisBimbingan) {
        setError("Pilih jenis bimbingan!");
        return;
      }
      
      // Cek apakah dosen yang dipilih memiliki sesi yang masih pending
      // CRITICAL: Only consider non-deleted pending sessions
      const pendingSessions = bimbinganList.filter(b => 
        b.status === 'pending' && 
        b.pembimbingId === selectedPembimbing &&
        b.deleted !== true
      );
      
      if (pendingSessions.length > 0) {
        setError(`Anda tidak dapat menambahkan sesi bimbingan baru untuk dosen yang dipilih karena masih ada sesi sebelumnya yang belum direview.`);
        return;
      }
      
      // Validasi progres berdasarkan jenis bimbingan
      // Cek status seminar untuk validasi
      try {
        // Jika jenis bimbingan adalah 'hasil', validasi bahwa seminar proposal sudah selesai dan LULUS
        if (jenisBimbingan === 'hasil') {
          let proposalSnapshot = null;
          
          // Query untuk cek seminar proposal - coba dengan orderBy dulu, jika gagal pakai tanpa orderBy
          try {
            const proposalQuery = query(
              collection(db, "seminar"),
              where("tugasAkhirId", "==", tugasAkhir.id),
              where("jenis", "==", "proposal"),
              orderBy("completedAt", "desc")
            );
            proposalSnapshot = await getDocs(proposalQuery);
          } catch (orderByError) {
            // Jika orderBy gagal (misalnya tidak ada index), coba tanpa orderBy
            console.log("OrderBy failed for proposal validation, trying without orderBy:", orderByError);
            const proposalQueryWithoutOrder = query(
              collection(db, "seminar"),
              where("tugasAkhirId", "==", tugasAkhir.id),
              where("jenis", "==", "proposal")
            );
            proposalSnapshot = await getDocs(proposalQueryWithoutOrder);
          }
          
          if (!proposalSnapshot || proposalSnapshot.empty) {
            setError("Anda belum melakukan seminar proposal. Tidak dapat memulai bimbingan hasil.");
            return;
          }
          
          // Sort by completedAt manually if available, or by tanggal
          let proposalDocs = [...proposalSnapshot.docs];
          proposalDocs.sort((a, b) => {
            const aData = a.data();
            const bData = b.data();
            const aDate = aData.completedAt || aData.tanggal || '';
            const bDate = bData.completedAt || bData.tanggal || '';
            return bDate.localeCompare(aDate);
          });
          
          // Find the most recent completed seminar (not superseded or cancelled)
          let proposalData = null;
          for (const doc of proposalDocs) {
            const data = doc.data();
            if (data.status === 'completed' && !data.superseded && !data.cancelled) {
              proposalData = data;
              break;
            }
          }
          
          // Fallback: use first document if no non-superseded found
          if (!proposalData && proposalDocs.length > 0) {
            proposalData = proposalDocs[0].data();
          }
          
          if (!proposalData) {
            setError("Tidak ada data seminar proposal yang valid ditemukan.");
            return;
          }
          
          const proposalCompleted = proposalData.status === 'completed';
          
          if (!proposalCompleted) {
            setError("Seminar proposal Anda belum selesai. Tidak dapat memulai bimbingan hasil.");
            return;
          }
          
          // Check if student passed with score >= 60 using the utility function
          if (proposalData.nilai) {
            const pengujiScores = Object.values(proposalData.nilai);
            
            if (pengujiScores.length === 4) {
              // Use the standard utility function to calculate score
              const scoreResult = calculateSeminarScore(proposalData);
              const totalScore = scoreResult.finalScore;
              
              // Student must pass with score >= 60
              if (!scoreResult.isPassed) {
                setError(`Anda tidak lulus seminar proposal (nilai ${totalScore.toFixed(1)} < 60). Anda perlu mengajukan penjadwalan ulang seminar proposal.`);
                return;
              }
            } else {
              setError("Seminar proposal belum dinilai oleh semua penguji. Tidak dapat memulai bimbingan hasil.");
              return;
            }
          } else {
            setError("Seminar proposal belum dinilai. Tidak dapat memulai bimbingan hasil.");
            return;
          }
        }
        
        // Jika jenis bimbingan adalah 'finalisasi', validasi bahwa seminar hasil sudah selesai dan LULUS
        if (jenisBimbingan === 'finalisasi') {
          let hasilSnapshot = null;
          
          // Query untuk cek seminar hasil - coba dengan orderBy dulu, jika gagal pakai tanpa orderBy
          try {
            const hasilQuery = query(
              collection(db, "seminar"),
              where("tugasAkhirId", "==", tugasAkhir.id),
              where("jenis", "==", "hasil"),
              orderBy("completedAt", "desc")
            );
            hasilSnapshot = await getDocs(hasilQuery);
          } catch (orderByError) {
            // Jika orderBy gagal (misalnya tidak ada index), coba tanpa orderBy
            console.log("OrderBy failed for hasil validation, trying without orderBy:", orderByError);
            const hasilQueryWithoutOrder = query(
              collection(db, "seminar"),
              where("tugasAkhirId", "==", tugasAkhir.id),
              where("jenis", "==", "hasil")
            );
            hasilSnapshot = await getDocs(hasilQueryWithoutOrder);
          }
          
          if (!hasilSnapshot || hasilSnapshot.empty) {
            setError("Anda belum melakukan seminar hasil. Tidak dapat memulai bimbingan finalisasi.");
            return;
          }
          
          // Sort by completedAt manually if available, or by tanggal
          let hasilDocs = [...hasilSnapshot.docs];
          hasilDocs.sort((a, b) => {
            const aData = a.data();
            const bData = b.data();
            const aDate = aData.completedAt || aData.tanggal || '';
            const bDate = bData.completedAt || bData.tanggal || '';
            return bDate.localeCompare(aDate);
          });
          
          // Find the most recent completed seminar (not superseded or cancelled)
          let hasilData = null;
          for (const doc of hasilDocs) {
            const data = doc.data();
            if (data.status === 'completed' && !data.superseded && !data.cancelled) {
              hasilData = data;
              break;
            }
          }
          
          // Fallback: use first document if no non-superseded found
          if (!hasilData && hasilDocs.length > 0) {
            hasilData = hasilDocs[0].data();
          }
          
          if (!hasilData) {
            setError("Tidak ada data seminar hasil yang valid ditemukan.");
            return;
          }
          
          const hasilCompleted = hasilData.status === 'completed';
          
          if (!hasilCompleted) {
            setError("Seminar hasil Anda belum selesai. Tidak dapat memulai bimbingan finalisasi.");
            return;
          }
          
          // Check if student passed with score >= 60 using the utility function
          if (hasilData.nilai) {
            const pengujiScores = Object.values(hasilData.nilai);
            
            if (pengujiScores.length === 4) {
              // Use the standard utility function to calculate score
              const scoreResult = calculateSeminarScore(hasilData);
              const totalScore = scoreResult.finalScore;
              
              // Student must pass with score >= 60
              if (!scoreResult.isPassed) {
                setError(`Anda tidak lulus seminar hasil (nilai ${totalScore.toFixed(1)} < 60). Anda perlu mengajukan penjadwalan ulang seminar hasil.`);
                return;
              }
            } else {
              setError("Seminar hasil belum dinilai oleh semua penguji. Tidak dapat memulai bimbingan finalisasi.");
              return;
            }
          } else {
            setError("Seminar hasil belum dinilai. Tidak dapat memulai bimbingan finalisasi.");
            return;
          }
        }
      } catch (error) {
        console.error("Error validating seminar status:", error);
        console.error("Error details:", {
          message: error.message,
          code: error.code,
          stack: error.stack
        });
        setError(`Terjadi kesalahan saat validasi status seminar: ${error.message}. Silakan coba lagi atau hubungi administrator jika masalah berlanjut.`);
        return;
      }
      
      setLoading(true);
      
      // Hitung sesi baru berdasarkan dosen dan jenis bimbingan yang dipilih
      let sesi = 1;
      if (bimbinganList.length > 0) {
        // Filter bimbingan untuk dosen yang dipilih dengan jenis bimbingan yang sama
        const dosenBimbingan = bimbinganList.filter(
          b => b.pembimbingId === selectedPembimbing && b.jenisBimbingan === jenisBimbingan
        );
        
        if (dosenBimbingan.length > 0) {
          // Ambil sesi maksimum untuk dosen dan jenis bimbingan ini
          const maxSesi = Math.max(...dosenBimbingan.map(b => b.sesi || 0));
          sesi = maxSesi + 1;
        }
      }
      
      // Format tanggal dengan benar untuk Firestore
      const tanggalBimbinganFormatted = new Date(tanggalBimbingan).toISOString();
      const tanggalRevisiFormatted = new Date(tanggalRevisi).toISOString();
      const createdAtFormatted = new Date().toISOString();
      
      // Data bimbingan
      const bimbinganData = {
        tugasAkhirId: tugasAkhir.id,
        mahasiswaId: currentUser.uid,
        pembimbingId: selectedPembimbing,
        sesi: sesi,
        judul: `Bimbingan ${jenisBimbingan.charAt(0).toUpperCase() + jenisBimbingan.slice(1)} Sesi ${sesi}`,
        jenisBimbingan: jenisBimbingan,
        fileURL: catatan,
        fileName: '',
        catatan: catatanDospem || '',
        status: 'pending',
        tanggal: tanggalBimbinganFormatted,
        tanggalRevisi: tanggalRevisiFormatted,
        reviewCatatan: '',
        reviewDriveLink: '',
        tanggalReview: null,
        createdAt: createdAtFormatted
      };
      
      console.log("Data bimbingan yang akan disimpan:", bimbinganData);
      
      // Simpan ke Firestore
      const docRef = await addDoc(collection(db, "bimbingan"), bimbinganData);
      console.log("Bimbingan berhasil disimpan dengan ID:", docRef.id);
      
      setSuccess("Sesi bimbingan berhasil ditambahkan, menunggu verifikasi dosen.");
      setShowAddModal(false);
      setCatatan('');
      setSelectedPembimbing('');
      setTanggalBimbingan('');
      setCatatanDospem('');
      setTanggalRevisi('');
      
      // Refresh data
      fetchData();
    } catch (error) {
      console.error("Error adding bimbingan:", error);
      setError("Terjadi kesalahan saat menyimpan sesi bimbingan: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // handleReviewSubmit has been removed and merged with handleSaveEdit

  const handleShowDetail = (bimbingan) => {
    setSelectedBimbingan(bimbingan);
    setShowDetailModal(true);
  };

  // handleShowReview has been removed and merged with handleShowEdit

  const handleShowEdit = (bimbingan, isReview = false) => {
    console.log('Edit clicked', bimbingan, isReview);
    setSelectedEditBimbingan(bimbingan);
    setSelectedBimbingan(null); // Clear the detail view if open
    setShowReviewModal(false); // Close the review modal if open
    setShowEditModal(true);
  };

  const handleSaveEdit = async (bimbinganBaru) => {
    setLoadingEdit(true);
    setErrorEdit('');
    try {
      // Debug log untuk memastikan data benar
      console.log('handleSaveEdit data:', bimbinganBaru);
      
      // Build the update data object
      const updateData = {
        judul: bimbinganBaru.judul,
        fileURL: bimbinganBaru.fileURL,
        tanggal: bimbinganBaru.tanggal,
        revisiPoin: Array.isArray(bimbinganBaru.revisiPoin) ? bimbinganBaru.revisiPoin : [],
      };
      
      // Add review status if provided
      if (bimbinganBaru.status === 'reviewed') {
        updateData.status = 'reviewed';
        updateData.tanggalReview = bimbinganBaru.tanggalReview || new Date().toISOString();
      }
      
      await updateDoc(doc(db, "bimbingan", bimbinganBaru.id), updateData);
      setShowEditModal(false);
      setSelectedEditBimbingan(null);
      fetchData();
      
      // Set different success message based on action
      if (bimbinganBaru.status === 'reviewed' && bimbinganBaru?.tugasAkhir?.mahasiswa?.nama) {
        setSuccess(`Sesi bimbingan untuk ${bimbinganBaru.tugasAkhir.mahasiswa.nama} berhasil direview`);
      } else {
        setSuccess('Sesi bimbingan berhasil diperbarui');
      }
    } catch (err) {
      setErrorEdit('Gagal menyimpan perubahan: ' + err.message);
    } finally {
      setLoadingEdit(false);
    }
  };


  const renderBimbinganTable = () => {
    // Admin view: No need for complex deduplication/grouping logic initially, just display all
    if (userRole === 'pj') {
      return renderBimbinganTableForAdmin(bimbinganList);
    }
    
    // Existing logic for mahasiswa and dosen
    // Deduplicate bimbingan records based on session, tugasAkhirId and pembimbingId
    const uniqueBimbingans = [];
    const seenKeys = new Set();
    
    // Filter out deleted sessions and handle deduplication efficiently
    const nonDeletedBimbingan = bimbinganList.filter(b => b.deleted !== true);
    
    // Process each bimbingan and only keep unique ones based on composite key
    nonDeletedBimbingan.forEach(bimbingan => {
      // Create a unique key using combination of properties 
      const key = `${bimbingan.tugasAkhirId}-${bimbingan.pembimbingId}-${bimbingan.sesi}-${bimbingan.jenisBimbingan || 'proposal'}`;
      
      // Skip deleted sessions and handle duplicates
      if (bimbingan.deleted === true) {
        // Skip deleted sessions
      } else if (!seenKeys.has(key)) {
        seenKeys.add(key);
        uniqueBimbingans.push(bimbingan);
      } else {
        // For duplicates, keep the most recent version
        const existingIndex = uniqueBimbingans.findIndex(b => {
          const bKey = `${b.tugasAkhirId}-${b.pembimbingId}-${b.sesi}-${b.jenisBimbingan || 'proposal'}`;
          return bKey === key;
        });
        
        if (existingIndex >= 0) {
          const existing = uniqueBimbingans[existingIndex];
          const existingDate = existing.createdAt ? new Date(existing.createdAt) : new Date(0);
          const currentDate = bimbingan.createdAt ? new Date(bimbingan.createdAt) : new Date(0);
          
          if (currentDate > existingDate) {
            uniqueBimbingans[existingIndex] = bimbingan;
          }
        }
      }
    });
    
    // Use the shared utility function to count sessions by type
    const bimbinganCounts = countBimbinganSessions(uniqueBimbingans);
    
    // If we have pembimbing info, calculate counts for each pembimbing
    if (userRole === 'mahasiswa' && Array.isArray(tugasAkhir?.pembimbing)) {
      tugasAkhir.pembimbing.forEach((p) => {
        const pembimbingId = p.id;
        const sessionsByPembimbing = uniqueBimbingans.filter(b => b.pembimbingId === pembimbingId);
        
        // Use the same utility for each pembimbing's sessions
        const pembimbingCounts = countBimbinganSessions(sessionsByPembimbing);
        
        // Store counts for display
        p.sessionCounts = pembimbingCounts;
      });
    }
    
    // For mahasiswa, we'll show all bimbingan (both reviewed and pending)
    // For dosen, no change in behavior
    const reviewedList = userRole === 'mahasiswa' ? 
      uniqueBimbingans.filter(b => b.status === 'reviewed') : 
      uniqueBimbingans;
      
    const pendingList = userRole === 'mahasiswa' ? 
      uniqueBimbingans.filter(b => b.status === 'pending') : 
      [];
      
    // Full list of all bimbingan for display purposes
    const fullBimbinganList = userRole === 'mahasiswa' ? uniqueBimbingans : [];
    
    // For mahasiswa: Group bimbingan by pembimbing and then by jenis
    if (userRole === 'mahasiswa' && Array.isArray(tugasAkhir?.pembimbing) && tugasAkhir.pembimbing.length > 0) {
      // Pending list for alert notifications
      const pendingByDosen = {};
      pendingList.forEach(b => {
        pendingByDosen[b.pembimbingId] = b;
      });
      
      // Get lists per pembimbing
      const pembimbingIds = tugasAkhir.pembimbing.map(p => p.id);
      
      // Group bimbingan by pembimbing and then by jenis
      const bimbinganByPembimbing = {};
      
      // First pembimbing (P1)
      if (pembimbingIds[0]) {
        const dosenId = pembimbingIds[0];
        bimbinganByPembimbing[dosenId] = {
          pembimbingIndex: 0,
          // Show only reviewed sessions for progress tracking
          proposal: reviewedList.filter(b => b.pembimbingId === dosenId && (b.jenisBimbingan === 'proposal' || !b.jenisBimbingan)),
          hasil: reviewedList.filter(b => b.pembimbingId === dosenId && b.jenisBimbingan === 'hasil'),
          finalisasi: reviewedList.filter(b => b.pembimbingId === dosenId && b.jenisBimbingan === 'finalisasi'),
          // Keep track of pending sessions too
          pendingProposal: pendingList.filter(b => b.pembimbingId === dosenId && (b.jenisBimbingan === 'proposal' || !b.jenisBimbingan)),
          pendingHasil: pendingList.filter(b => b.pembimbingId === dosenId && b.jenisBimbingan === 'hasil'),
          pendingFinalisasi: pendingList.filter(b => b.pembimbingId === dosenId && b.jenisBimbingan === 'finalisasi'),
          // Combined lists for display
          allProposal: uniqueBimbingans.filter(b => b.pembimbingId === dosenId && (b.jenisBimbingan === 'proposal' || !b.jenisBimbingan)),
          allHasil: uniqueBimbingans.filter(b => b.pembimbingId === dosenId && b.jenisBimbingan === 'hasil'),
          allFinalisasi: uniqueBimbingans.filter(b => b.pembimbingId === dosenId && b.jenisBimbingan === 'finalisasi')
        };
      }
      
      // Second pembimbing (P2)
      if (pembimbingIds[1]) {
        const dosenId = pembimbingIds[1];
        bimbinganByPembimbing[dosenId] = {
          pembimbingIndex: 1,
          // Show only reviewed sessions for progress tracking
          proposal: reviewedList.filter(b => b.pembimbingId === dosenId && (b.jenisBimbingan === 'proposal' || !b.jenisBimbingan)),
          hasil: reviewedList.filter(b => b.pembimbingId === dosenId && b.jenisBimbingan === 'hasil'),
          finalisasi: reviewedList.filter(b => b.pembimbingId === dosenId && b.jenisBimbingan === 'finalisasi'),
          // Keep track of pending sessions too
          pendingProposal: pendingList.filter(b => b.pembimbingId === dosenId && (b.jenisBimbingan === 'proposal' || !b.jenisBimbingan)),
          pendingHasil: pendingList.filter(b => b.pembimbingId === dosenId && b.jenisBimbingan === 'hasil'),
          pendingFinalisasi: pendingList.filter(b => b.pembimbingId === dosenId && b.jenisBimbingan === 'finalisasi'),
          // Combined lists for display
          allProposal: uniqueBimbingans.filter(b => b.pembimbingId === dosenId && (b.jenisBimbingan === 'proposal' || !b.jenisBimbingan)),
          allHasil: uniqueBimbingans.filter(b => b.pembimbingId === dosenId && b.jenisBimbingan === 'hasil'),
          allFinalisasi: uniqueBimbingans.filter(b => b.pembimbingId === dosenId && b.jenisBimbingan === 'finalisasi')
        };
      }
      
      // Show a summary of pending items at top
      if (Object.keys(pendingByDosen).length > 0) {
        const pendingItems = Object.values(pendingByDosen);
        return (
          <>
            {/* Summary alert for pending sessions */}
            <Alert variant="warning" className="mb-3">
              <h6>Bimbingan sedang direview:</h6>
              <ul className="mb-0">
                {pendingItems.map((pendingItem, idx) => (
                  <li key={idx}>
                    <strong>Sesi {pendingItem.sesi} ({pendingItem.jenisBimbingan ? pendingItem.jenisBimbingan.charAt(0).toUpperCase() + pendingItem.jenisBimbingan.slice(1) : 'Proposal'}) - Pembimbing: {Array.isArray(tugasAkhir?.pembimbing) 
                      ? (tugasAkhir.pembimbing.find(p => p.id === pendingItem.pembimbingId)?.nama || '-')
                      : (tugasAkhir?.pembimbingNama || '-')
                    }</strong> 
                    <small className="ms-2">Diupload: {new Date(pendingItem.tanggal).toLocaleDateString()} (estimasi review: 3 hari)</small>
                  </li>
                ))}
              </ul>
            </Alert>
            
            {/* Render tables by pembimbing - showing all bimbingan (pending and reviewed) */}
            {Object.values(bimbinganByPembimbing).map((pembimbingData) => (
              <div key={`pembimbing-${pembimbingData.pembimbingIndex}`} className="mb-5">
                <h5>Bimbingan dengan {tugasAkhir.pembimbing[pembimbingData.pembimbingIndex].nama} (P{pembimbingData.pembimbingIndex + 1})</h5>
                
                {/* Proposal */}
                <div className="mb-4">
                  <h6 className="mb-3">
                    Bimbingan Proposal {pembimbingData.pembimbingIndex === 0 && (
                      // CRITICAL: Use consistent logic with MahasiswaBimbingan.js - min 5 for proposal
                      pembimbingData.proposal.length >= 5 ? 
                        <Badge bg="success" className="ms-2">Siap Ajukan Seminar</Badge> : 
                        <Badge bg="warning" className="ms-2">Butuh {5 - pembimbingData.proposal.length} sesi lagi</Badge>
                    )}
                    {pembimbingData.pendingProposal?.length > 0 && (
                      <Badge bg="info" className="ms-2">{pembimbingData.pendingProposal.length} sesi menunggu review</Badge>
                    )}
                  </h6>
                  {renderBimbinganTableForType(pembimbingData.pembimbingIndex, pembimbingData.allProposal, 'proposal')}
                </div>
                
                {/* Hasil */}
                <div className="mb-4">
                  <h6 className="mb-3">
                    Bimbingan Hasil {pembimbingData.pembimbingIndex === 0 && (
                      // CRITICAL: Use consistent logic with MahasiswaBimbingan.js - min 5 for hasil
                      pembimbingData.hasil.length >= 5 ? 
                        <Badge bg="success" className="ms-2">Siap Ajukan Seminar</Badge> : 
                        <Badge bg="warning" className="ms-2">Butuh {5 - pembimbingData.hasil.length} sesi lagi</Badge>
                    )}
                    {pembimbingData.pendingHasil?.length > 0 && (
                      <Badge bg="info" className="ms-2">{pembimbingData.pendingHasil.length} sesi menunggu review</Badge>
                    )}
                  </h6>
                  {renderBimbinganTableForType(pembimbingData.pembimbingIndex, pembimbingData.allHasil, 'hasil')}
                </div>
                
                {/* Finalisasi */}
                <div className="mb-4">
                  <h6 className="mb-3">
                    Bimbingan Finalisasi {pembimbingData.pembimbingIndex === 0 && (
                      // CRITICAL: Use consistent logic with MahasiswaBimbingan.js - min 2 for finalisasi
                      pembimbingData.finalisasi.length >= 2 ? 
                        <Badge bg="success" className="ms-2">Siap Ajukan Sidang</Badge> : 
                        <Badge bg="warning" className="ms-2">Butuh {2 - pembimbingData.finalisasi.length} sesi lagi</Badge>
                    )}
                    {pembimbingData.pendingFinalisasi?.length > 0 && (
                      <Badge bg="info" className="ms-2">{pembimbingData.pendingFinalisasi.length} sesi menunggu review</Badge>
                    )}
                  </h6>
                  {renderBimbinganTableForType(pembimbingData.pembimbingIndex, pembimbingData.allFinalisasi, 'finalisasi')}
                </div>
              </div>
            ))}
          </>
        );
      }
      
      // Show tables without pending alerts - but still show all sessions
      return (
        <>
          {/* Render tables by pembimbing */}
          {Object.values(bimbinganByPembimbing).map((pembimbingData) => (
            <div key={`pembimbing-${pembimbingData.pembimbingIndex}`} className="mb-5">
              <h5>Bimbingan dengan {tugasAkhir.pembimbing[pembimbingData.pembimbingIndex].nama} (P{pembimbingData.pembimbingIndex + 1})</h5>
              
              {/* Proposal */}
              <div className="mb-4">
                <h6 className="mb-3">
                  Bimbingan Proposal {pembimbingData.pembimbingIndex === 0 && (
                    // CRITICAL: Use consistent logic with MahasiswaBimbingan.js - min 5 for proposal
                    pembimbingData.proposal.length >= 5 ? 
                      <Badge bg="success" className="ms-2">Siap Ajukan Seminar</Badge> : 
                      <Badge bg="warning" className="ms-2">Butuh {5 - pembimbingData.proposal.length} sesi lagi</Badge>
                  )}
                </h6>
                {renderBimbinganTableForType(pembimbingData.pembimbingIndex, pembimbingData.allProposal, 'proposal')}
              </div>
              
              {/* Hasil */}
              <div className="mb-4">
                <h6 className="mb-3">
                  Bimbingan Hasil {pembimbingData.pembimbingIndex === 0 && (
                    // CRITICAL: Use consistent logic with MahasiswaBimbingan.js - min 5 for hasil
                    pembimbingData.hasil.length >= 5 ? 
                      <Badge bg="success" className="ms-2">Siap Ajukan Seminar</Badge> : 
                      <Badge bg="warning" className="ms-2">Butuh {5 - pembimbingData.hasil.length} sesi lagi</Badge>
                  )}
                </h6>
                {renderBimbinganTableForType(pembimbingData.pembimbingIndex, pembimbingData.allHasil, 'hasil')}
              </div>
              
              {/* Finalisasi */}
              <div className="mb-4">
                <h6 className="mb-3">
                  Bimbingan Finalisasi {pembimbingData.pembimbingIndex === 0 && (
                    // CRITICAL: Use consistent logic with MahasiswaBimbingan.js - min 2 for finalisasi
                    pembimbingData.finalisasi.length >= 2 ? 
                      <Badge bg="success" className="ms-2">Siap Ajukan Sidang</Badge> : 
                      <Badge bg="warning" className="ms-2">Butuh {2 - pembimbingData.finalisasi.length} sesi lagi</Badge>
                  )}
                </h6>
                {renderBimbinganTableForType(pembimbingData.pembimbingIndex, pembimbingData.allFinalisasi, 'finalisasi')}
              </div>
            </div>
          ))}
        </>
      );
    }
    
    // For Dosen: Group bimbingan by jenis
    if (userRole === 'dosen') {
      // Group bimbingan by mahasiswaId and then by jenis
      const byMahasiswa = {};
      
      reviewedList.forEach(bimbingan => {
        const mahasiswaId = bimbingan.mahasiswaId || 'unknown';
        if (!byMahasiswa[mahasiswaId]) {
          byMahasiswa[mahasiswaId] = {
            mahasiswa: bimbingan.tugasAkhir?.mahasiswa || { nama: bimbingan.mahasiswaNama || 'Mahasiswa' },
            proposal: [],
            hasil: [],
            finalisasi: []
          };
        }
        
        // Add to appropriate category based on jenisBimbingan
        const jenis = bimbingan.jenisBimbingan || 'proposal';
        byMahasiswa[mahasiswaId][jenis].push(bimbingan);
      });
      
      return (
        <>
          {Object.values(byMahasiswa).map((data, index) => (
            <div key={`mahasiswa-${index}`} className="mb-5">
              <h5>Bimbingan {data.mahasiswa.nama || 'Mahasiswa'}</h5>
              
              {/* Proposal */}
              <div className="mb-4">
                <h6 className="mb-3">Bimbingan Proposal {/* CRITICAL: Use consistent logic with MahasiswaBimbingan.js - min 5 for proposal */}
                {data.proposal.length >= 5 ? 
                  <Badge bg="success" className="ms-2">Siap Seminar</Badge> : 
                  <Badge bg="warning" className="ms-2">Butuh {5 - data.proposal.length} sesi lagi</Badge>
                }</h6>
                {renderBimbinganTableForType(-1, data.proposal, 'proposal')}
              </div>
              
              {/* Hasil */}
              <div className="mb-4">
                <h6 className="mb-3">Bimbingan Hasil {/* CRITICAL: Use consistent logic with MahasiswaBimbingan.js - min 5 for hasil */}
                {data.hasil.length >= 5 ? 
                  <Badge bg="success" className="ms-2">Siap Seminar</Badge> : 
                  <Badge bg="warning" className="ms-2">Butuh {5 - data.hasil.length} sesi lagi</Badge>
                }</h6>
                {renderBimbinganTableForType(-1, data.hasil, 'hasil')}
              </div>
              
              {/* Finalisasi */}
              <div className="mb-4">
                <h6 className="mb-3">Bimbingan Finalisasi {/* CRITICAL: Use consistent logic with MahasiswaBimbingan.js - min 2 for finalisasi */}
                {data.finalisasi.length >= 2 ? 
                  <Badge bg="success" className="ms-2">Siap Sidang</Badge> : 
                  <Badge bg="warning" className="ms-2">Butuh {2 - data.finalisasi.length} sesi lagi</Badge>
                }</h6>
                {renderBimbinganTableForType(-1, data.finalisasi, 'finalisasi')}
              </div>
            </div>
          ))}
        </>
      );
    }
    
    // Fallback to old behavior for other cases
    return renderBimbinganTableForDosen(-1, reviewedList);
  };
  
  // Helper function to render a single bimbingan table by type (proposal, hasil, finalisasi) for Mahasiswa/Dosen
  const renderBimbinganTableForType = (pembimbingIdx, bimbinganData, jenisBimbingan) => {
    const isDosenView = userRole === 'dosen';
    
    return (
      <div>
        {bimbinganData.length === 0 ? (
          <Alert variant="info">
            Belum ada sesi bimbingan {jenisBimbingan}
          </Alert>
        ) : (
          <Table responsive striped bordered hover>
            <thead>
              <tr>
                <th>Sesi</th>
                {isDosenView && <th>Mahasiswa</th>}
                <th>Judul</th>
                <th>Tanggal Upload</th>
                <th>Status</th>
                <th>Tanggal Review</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {bimbinganData.map((bimbingan, index) => (
                <tr key={`bimbingan-${bimbingan.id || index}`} className={bimbingan.status === 'pending' ? 'table-warning' : ''}>
                  <td>{bimbingan.sesi}</td>
                  {isDosenView && (
                    <td>
                      {bimbingan.tugasAkhir?.mahasiswa?.nama || '-'}
                      <br />
                      <small>{bimbingan.tugasAkhir?.mahasiswa?.nim || '-'}</small>
                    </td>
                  )}
                  <td>{bimbingan.judul}</td>
                  <td>{bimbingan.tanggal ? new Date(bimbingan.tanggal).toLocaleDateString() : '-'}</td>
                  <td>
                    {bimbingan.status === 'reviewed' ? (
                      <Badge bg="success">Sudah Direview</Badge>
                    ) : (
                      <div>
                        <Badge bg="warning">Menunggu Review</Badge>
                        <div className="mt-1 small text-muted">
                          Diupload: {new Date(bimbingan.tanggal).toLocaleDateString()}
                        </div>
                      </div>
                    )}
                  </td>
                  <td>
                    {bimbingan.tanggalReview 
                      ? new Date(bimbingan.tanggalReview).toLocaleDateString() 
                      : '-'}
                  </td>
                  <td>
                    <div className="d-flex gap-2">
                      <Button 
                        variant="outline-info" 
                        size="sm"
                        onClick={() => handleShowDetail(bimbingan)}
                        title="Lihat Detail Bimbingan"
                      >
                        <FaEye />
                      </Button>
                      {isDosenView && (
                        <>
                          <Button 
                            variant={bimbingan.status === 'pending' ? "outline-success" : "outline-warning"}
                            size="sm"
                            onClick={() => handleShowEdit(bimbingan, bimbingan.status === 'pending')}
                            title={bimbingan.status === 'pending' ? "Edit & Review" : "Edit Bimbingan"}
                          >
                            <FaEdit />
                          </Button>
                          <Button 
                            variant="outline-danger" 
                            size="sm"
                            onClick={() => handleDeleteBimbingan(bimbingan.id)}
                            title="Hapus Bimbingan"
                          >
                        <FaTrash />
                      </Button>
                    </>
                  )}
                  {/* Admin can also delete */}
                  {userRole === 'pj' && (
                     <Button 
                       variant="outline-danger" 
                       size="sm"
                       onClick={() => handleDeleteBimbingan(bimbingan.id)}
                       title="Hapus Bimbingan"
                     >
                       <FaTrash />
                     </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    )}
  </div>
);
};

// Helper function to render the table for Admin (PJ)
const renderBimbinganTableForAdmin = (bimbinganData) => {
  if (bimbinganData.length === 0) {
    return <Alert variant="info">Belum ada data bimbingan.</Alert>;
  }
  
  return (
    <Table responsive striped bordered hover>
      <thead>
        <tr>
          <th>Mahasiswa</th>
          <th>Dosen Pembimbing</th>
          <th>Jenis</th>
          <th>Sesi</th>
          <th>Judul</th>
          <th>Tanggal Upload</th>
          <th>Status</th>
          <th>Tanggal Review</th>
          <th>Aksi</th>
        </tr>
      </thead>
      <tbody>
        {bimbinganData.map((bimbingan, index) => (
          <tr key={`bimbingan-admin-${bimbingan.id || index}`} className={bimbingan.status === 'pending' ? 'table-warning' : ''}>
            <td>
              {bimbingan.mahasiswa?.nama || '-'}
              <br />
              <small>{bimbingan.mahasiswa?.nim || '-'}</small>
            </td>
            <td>{bimbingan.dosen?.nama || '-'}</td>
            <td>{bimbingan.jenisBimbingan ? bimbingan.jenisBimbingan.charAt(0).toUpperCase() + bimbingan.jenisBimbingan.slice(1) : 'Proposal'}</td>
            <td>{bimbingan.sesi}</td>
            <td>{bimbingan.judul}</td>
            <td>{bimbingan.tanggal ? new Date(bimbingan.tanggal).toLocaleDateString() : '-'}</td>
            <td>
              {bimbingan.status === 'reviewed' ? (
                <Badge bg="success">Sudah Direview</Badge>
              ) : (
                <Badge bg="warning">Menunggu Review</Badge>
              )}
            </td>
            <td>
              {bimbingan.tanggalReview 
                ? new Date(bimbingan.tanggalReview).toLocaleDateString() 
                : '-'}
            </td>
            <td>
              <div className="d-flex gap-2">
                <Button 
                  variant="outline-info" 
                  size="sm"
                  onClick={() => handleShowDetail(bimbingan)}
                  title="Lihat Detail Bimbingan"
                >
                  <FaEye />
                </Button>
                {/* Admin can edit */}
                <Button 
                  variant={bimbingan.status === 'pending' ? "outline-success" : "outline-warning"}
                  size="sm"
                  onClick={() => handleShowEdit(bimbingan, bimbingan.status === 'pending')}
                  title={bimbingan.status === 'pending' ? "Edit & Review" : "Edit Bimbingan"}
                >
                  <FaEdit />
                </Button>
                {/* Admin can delete */}
                <Button 
                  variant="outline-danger" 
                  size="sm"
                  onClick={() => handleDeleteBimbingan(bimbingan.id)}
                  title="Hapus Bimbingan"
                >
                  <FaTrash />
                </Button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

// Helper function to render a single bimbingan table for a dosen (original function, kept for compatibility)
const renderBimbinganTableForDosen = (pembimbingIdx, bimbinganData) => {
  const isDosenView = userRole === 'dosen';
    const dosenInfo = pembimbingIdx >= 0 && Array.isArray(tugasAkhir?.pembimbing) 
      ? tugasAkhir.pembimbing[pembimbingIdx] 
      : null;
      
    const title = dosenInfo 
      ? `Bimbingan dengan ${dosenInfo.nama} (P${pembimbingIdx + 1})` 
      : "Daftar Bimbingan";
    
    // Group bimbingan by type
    const proposal = bimbinganData.filter(b => b.jenisBimbingan === 'proposal' || !b.jenisBimbingan);
    const hasil = bimbinganData.filter(b => b.jenisBimbingan === 'hasil');
    const finalisasi = bimbinganData.filter(b => b.jenisBimbingan === 'finalisasi');
    
    return (
      <div className="mb-4">
        {dosenInfo && <h5 className="mb-3">{title}</h5>}
        
        {bimbinganData.length === 0 ? (
          <Alert variant="info">
            {dosenInfo 
              ? `Belum ada sesi bimbingan dengan ${dosenInfo.nama}`
              : "Belum ada sesi bimbingan"
            }
          </Alert>
        ) : (
          <div>
            {/* Proposal */}
            <div className="mb-4">
              <h6 className="mb-3">Bimbingan Proposal</h6>
              {renderBimbinganTableForType(pembimbingIdx, proposal, 'proposal')}
            </div>
            
            {/* Hasil */}
            <div className="mb-4">
              <h6 className="mb-3">Bimbingan Hasil</h6>
              {renderBimbinganTableForType(pembimbingIdx, hasil, 'hasil')}
            </div>
            
            {/* Finalisasi */}
            <div className="mb-4">
              <h6 className="mb-3">Bimbingan Finalisasi</h6>
              {renderBimbinganTableForType(pembimbingIdx, finalisasi, 'finalisasi')}
            </div>
          </div>
        )}
      </div>
    );
  };

  const handleDeleteBimbingan = async (bimbinganId) => {
    if (!window.confirm('Yakin ingin menghapus sesi bimbingan ini?')) return;
    try {
      setLoading(true);
      
      // Instead of deleting, mark as deleted with a timestamp
      await updateDoc(doc(db, "bimbingan", bimbinganId), {
        deleted: true,
        deletedAt: new Date().toISOString()
      });
      
      setSuccess('Sesi bimbingan berhasil dihapus.');
      fetchData();
    } catch (err) {
      console.error("Error deleting bimbingan:", err);
      setError('Gagal menghapus sesi bimbingan.');
    } finally {
      setLoading(false);
    }
  };

  const renderAddBimbinganModal = () => {
    return (
      <Modal show={showAddModal} onHide={() => {
        setShowAddModal(false);
        resetAddBimbinganForm();
      }}>
        <Modal.Header closeButton>
          <Modal.Title>Tambah Sesi Bimbingan</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Jenis Bimbingan <span className="text-danger">*</span></Form.Label>
              <Form.Select
                value={jenisBimbingan}
                onChange={(e) => setJenisBimbingan(e.target.value)}
                required
              >
                {/* Tampilkan opsi berdasarkan progres seminar */}
                {!(proposalSeminarCompleted && proposalSeminarPassed) ? (
                  // Jika seminar proposal belum selesai/lulus, hanya tampilkan proposal
                  <>
                    <option value="proposal">Proposal</option>
                  </>
                ) : hasilSeminarCompleted && hasilSeminarPassed ? (
                  // Jika seminar hasil sudah selesai/lulus, tampilkan semua opsi (proposal, hasil, finalisasi)
                  <>
                    <option value="proposal">Proposal</option>
                    <option value="hasil">Hasil</option>
                    <option value="finalisasi">Finalisasi</option>
                  </>
                ) : (
                  // Jika proposal sudah selesai/lulus tapi hasil belum, tampilkan proposal dan hasil
                  <>
                    <option value="proposal">Proposal</option>
                    <option value="hasil">Hasil</option>
                  </>
                )}
              </Form.Select>
              <Form.Text className="text-muted">
                <ul className="mb-0 ps-3">
                  <li><b>Proposal:</b> Bimbingan untuk persiapan seminar proposal (min. 5 sesi)</li>
                  <li><b>Hasil:</b> Bimbingan untuk persiapan seminar hasil (min. 5 sesi)</li>
                  <li><b>Finalisasi:</b> Bimbingan untuk persiapan sidang komprehensif (min. 2 sesi)</li>
                </ul>
                <div className="mt-2">
                  <Alert variant="info" className="p-2 small">
                    <strong>Catatan:</strong> Bimbingan dilakukan secara bertahap berdasarkan progres:
                    <ul className="mb-0 ps-3 mt-1">
                      <li>Anda dapat menambah bimbingan proposal tanpa batasan jumlah</li>
                      <li>Bimbingan hasil hanya dapat dilakukan setelah seminar proposal selesai</li>
                      <li>Bimbingan finalisasi hanya dapat dilakukan setelah seminar hasil selesai</li>
                    </ul>
                  </Alert>
                </div>
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Link Google Drive Dokumen Bimbingan <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                value={catatan}
                onChange={handleLinkChange}
                placeholder={`Contoh: https://drive.google.com/..., dengan file bernama ${jenisBimbingan}_NIM_Nama mahasiswa`}
              />
              <Form.Text className="text-muted">
                {catatan ? 
                  "Link dari sesi sebelumnya telah dimasukkan otomatis. Anda dapat menggunakan link yang sama atau menggantinya." :
                  <>
                    Masukkan link Google Drive dokumen bimbingan. <br />
                    <b>Format nama file di Google Drive (sesi bimbingan {jenisBimbingan}):</b><br />
                    <ul className="mb-0 ps-3">
                      <li>Untuk bimbingan baru: <u>{jenisBimbingan}_NIM_Nama</u> (contoh: {jenisBimbingan}_123112312_Angga)</li>
                      <li>Untuk revisi: <u>PX_{jenisBimbingan}_revY_Nama</u> dengan X=nomor pembimbing, Y=nomor revisi (contoh: P1_{jenisBimbingan}_rev2_Angga)</li>
                    </ul>
                  </>
                }
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Pilih Dosen Pembimbing <span className="text-danger">*</span></Form.Label>
              <Form.Select 
                value={selectedPembimbing || ''} 
                onChange={e => {
                  const newPembimbingId = e.target.value;
                  setSelectedPembimbing(newPembimbingId);
                  
                  // Auto-fill link from last session with this dosen
                  const lastLink = getLastLinkForPembimbing(newPembimbingId);
                  if (lastLink) {
                    setCatatan(lastLink);
                  }
                }} 
                required
              >
                <option value="">Pilih Dosen Pembimbing</option>
                {Array.isArray(tugasAkhir?.pembimbing) && tugasAkhir.pembimbing.map((p, idx) => {
                  // Cek apakah dosen ini memiliki sesi pending (non-deleted)
                  const hasPendingSessions = getDosensWithPendingSessions().includes(p.id);
                  
                  // Count non-deleted, reviewed sessions with this pembimbing
                  const completedSessions = bimbinganList.filter(b => 
                    b.pembimbingId === p.id && 
                    b.status === 'reviewed' && 
                    b.deleted !== true
                  ).length;
                  
                  return (
                    <option 
                      key={p.id} 
                      value={p.id} 
                      disabled={hasPendingSessions}
                    >
                      P{idx+1}: {p.nama} {hasPendingSessions ? '(Menunggu Review)' : ''}
                    </option>
                  );
                })}
              </Form.Select>
              <Form.Text className="text-muted">
                Pilih dosen pembimbing yang akan mereview dokumen bimbingan ini.
                <br />
                <strong>Catatan:</strong> Dosen yang masih memiliki sesi bimbingan belum direview tidak dapat dipilih.
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Tanggal Bimbingan <span className="text-danger">*</span></Form.Label>
              <Form.Control 
                type="date" 
                value={tanggalBimbingan} 
                onChange={e => setTanggalBimbingan(e.target.value)} 
                required 
              />
              <Form.Text className="text-muted">
                Tanggal dokumen bimbingan diupload
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Tanggal Batas Revisi <span className="text-danger">*</span></Form.Label>
              <Form.Control 
                type="date" 
                value={tanggalRevisi} 
                onChange={e => setTanggalRevisi(e.target.value)} 
                required 
              />
              <Form.Text className="text-muted">
                Batas waktu yang diharapkan untuk mendapatkan review dari dosen
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Catatan untuk Dosen (opsional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={catatanDospem}
                onChange={e => setCatatanDospem(e.target.value)}
                placeholder="Masukkan catatan atau pertanyaan untuk dosen pembimbing (opsional)"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowAddModal(false);
            resetAddBimbinganForm();
          }}>
            Batal
          </Button>
          <Button 
            variant="primary" 
            onClick={handleAddBimbingan}
            disabled={loading || !catatan || !selectedPembimbing || !tanggalBimbingan || !tanggalRevisi}
          >
            {loading ? 'Menyimpan...' : 'Tambah Sesi'}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  const renderDetailModal = () => {
    if (!selectedBimbingan) return null;

    return (
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Detail Bimbingan</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <h5>Informasi Bimbingan</h5>
            <p><strong>Sesi:</strong> {selectedBimbingan.sesi}</p>
            <p><strong>Judul:</strong> {selectedBimbingan.judul}</p>
            <p><strong>Tanggal Upload:</strong> {new Date(selectedBimbingan.tanggal).toLocaleString()}</p>
            <p>
              <strong>Status:</strong>{' '}
              <Badge bg={selectedBimbingan.status === 'reviewed' ? 'success' : 'warning'}>
                {selectedBimbingan.status === 'reviewed' ? 'Sudah Direview' : 'Menunggu Review'}
              </Badge>
            </p>
            
            {userRole === 'dosen' && (
              <p>
                <strong>Mahasiswa:</strong>{' '}
                {selectedBimbingan.tugasAkhir?.mahasiswa?.nama || '-'} ({selectedBimbingan.tugasAkhir?.mahasiswa?.nim || '-'})
              </p>
            )}
            
            {userRole === 'mahasiswa' && (
              <p>
                <strong>Dosen Pembimbing:</strong>{' '}
                {Array.isArray(tugasAkhir?.pembimbing) 
                  ? tugasAkhir.pembimbing.find(p => p.id === selectedBimbingan.pembimbingId)?.nama || '-' 
                  : tugasAkhir?.pembimbingNama || '-'}
              </p>
            )}
            
            {selectedBimbingan.revisiPoin && (
              <div className="mt-3">
                <h6>Poin Revisi dari Dosen:</h6>
                <ul>
                  {Array.isArray(selectedBimbingan.revisiPoin)
                    ? selectedBimbingan.revisiPoin.filter(poin => poin && poin.trim() !== '').map((poin, idx) => (
                        <li key={idx}>{poin}</li>
                      ))
                    : (selectedBimbingan.revisiPoin && selectedBimbingan.revisiPoin.trim() !== '' && (
                        <li>{selectedBimbingan.revisiPoin}</li>
                      ))}
                </ul>
              </div>
            )}
          </div>
          
          <div className="d-flex justify-content-between mb-4">
            <div>
              <div>
                <strong>Link Dokumen Bimbingan:</strong>
                <Button 
                  variant="primary" 
                  href={selectedBimbingan.fileURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ms-2"
                >
                  <FaEye className="me-2" />
                  Lihat Dokumen
                </Button>
                <div className="mt-1 small text-muted">
                  {selectedBimbingan.status === 'reviewed' ? 
                    "Dokumen ini berisi hasil revisi dari dosen pembimbing" : 
                    "Dokumen ini masih menunggu review dari dosen pembimbing"}
                </div>
              </div>
            </div>
            
            {userRole === 'dosen' && (
              <Button 
                variant="success" 
                onClick={() => {
                  setShowDetailModal(false);
                  handleShowEdit(selectedBimbingan, selectedBimbingan.status === 'pending');
                }}
              >
                <FaEdit className="me-2" />
                {selectedBimbingan.status === 'pending' ? 'Edit & Review' : 'Edit Bimbingan'}
              </Button>
            )}
          </div>
          
          {selectedBimbingan.status === 'reviewed' && (
            <div className="mt-4">
              <h5>Informasi Review</h5>
              <p><strong>Tanggal Review:</strong> {new Date(selectedBimbingan.tanggalReview).toLocaleString()}</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
            Tutup
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  // renderReviewModal has been removed and merged with EditBimbinganModal

  return (
    <>
      {userRole === 'mahasiswa' && (!tugasAkhir || tugasAkhir.status !== 'approved') ? (
        <Container className="py-4">
          <Card>
            <Card.Body>
              <Card.Title>Bimbingan Tugas Akhir</Card.Title>
              <Alert variant="warning">
                {!tugasAkhir
                  ? "Anda belum mendaftar tugas akhir. Silakan daftar terlebih dahulu."
                  : "Tugas akhir Anda belum disetujui. Silakan tunggu persetujuan dari Penanggung Jawab Tugas Akhir."}
              </Alert>
            </Card.Body>
          </Card>
        </Container>
      ) : (
        <Container className="py-4">
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <Card.Title>
                  {userRole === 'pj' ? 'Riwayat Bimbingan (Semua)' : 'Bimbingan Tugas Akhir'}
                </Card.Title>
                <div>
                  {/* Refresh button for all roles */}
                  <Button 
                    className="mb-3 me-2" 
                    variant="outline-secondary"
                    onClick={() => {
                      setLoading(true);
                      fetchData().finally(() => setLoading(false));
                      setSuccess("Data bimbingan telah disegarkan");
                    }}
                  >
                    Segarkan Data
                  </Button>
                  
                  {userRole === 'dosen' && (
                    <>
                      <Button 
                        className="mb-3 me-2" 
                        variant="outline-info"
                        onClick={() => {
                          console.log("Informasi bimbingan dosen:", {
                            dosenId: currentUser.uid,
                            bimbinganList,
                            tugasAkhirList: bimbinganList.map(b => b.tugasAkhir).filter(Boolean)
                          });
                          setSuccess("Informasi diagnostik dicatat di konsol browser");
                        }}
                      >
                        Info Diagnostik
                      </Button>
                      <Button 
                        className="mb-3 me-2" 
                        variant="outline-secondary"
                        onClick={() => {
                          setLoading(true);
                          fetchData().finally(() => setLoading(false));
                          setSuccess("Data bimbingan telah disegarkan");
                        }}
                      >
                        Segarkan Data
                      </Button>
                      {/* Admin tools - hanya tampilkan untuk keperluan dev/admin */}
                      <Button 
                        className="mb-3 me-2" 
                        variant="outline-danger"
                        onClick={async () => {
                          setLoading(true);
                          try {
                            const result = await fixBimbinganData();
                            if (result.success) {
                              setSuccess(result.message);
                            } else {
                              setError(result.error);
                            }
                            fetchData();
                          } catch (error) {
                            setError("Error: " + error.message);
                          } finally {
                            setLoading(false);
                          }
                        }}
                      >
                        Perbaiki Data
                      </Button>
                    </>
                  )}
                  {userRole === 'mahasiswa' && (
                    <>
                      {getDosensWithPendingSessions().length === (Array.isArray(tugasAkhir?.pembimbing) ? tugasAkhir.pembimbing.length : 1) ? (
                        <Button className="mb-3" onClick={handleShowAddModal} variant="secondary" disabled>
                          <FaPlus className="me-2" />
                          Tambah Sesi (Tunggu Review Semua Dosen)
                        </Button>
                      ) : (
                        <Button className="mb-3" onClick={handleShowAddModal}>
                          <FaPlus className="me-2" />
                          {getDosensWithPendingSessions().length > 0 ? 
                            'Tambah Sesi (Beberapa Dosen Tersedia)' : 
                            'Tambah Sesi'}
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
              {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
              {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}
              
              {/* Hide TA info and rekap for Admin */}
              {userRole === 'mahasiswa' && tugasAkhir && (
                <div className="mb-4">
                  <Card className="mb-3">
                    <Card.Body>
                      <Card.Title>Rekap Jumlah Sesi Bimbingan</Card.Title>
                      <div className="row">
                        {Array.isArray(tugasAkhir.pembimbing) && tugasAkhir.pembimbing.map((p, idx) => {
                          // CRITICAL: Use a separate, clean calculation for the rekap section
                          console.log(`=== REKAP DEBUGGING FOR ${p.nama} (${p.id}) ===`);
                          
                          // First get all non-deleted bimbingan for this dosen
                          const cleanBimbingan = bimbinganList.filter(b => b.deleted !== true);
                          
                          console.log(`Total non-deleted bimbingan: ${cleanBimbingan.length}`);
                          console.log(`After first filter - pembimbing=${p.id}: ${cleanBimbingan.filter(b => b.pembimbingId === p.id).length}`);
                          
                          // Deduplicate by creating a unique key for each session
                          const uniqueKeys = new Set();
                          const dedupedBimbingan = [];
                          
                          cleanBimbingan.filter(b => b.pembimbingId === p.id).forEach(b => {
                            const key = `${p.id}-${b.jenisBimbingan || 'proposal'}-${b.sesi}`;
                            if (!uniqueKeys.has(key)) {
                              uniqueKeys.add(key);
                              dedupedBimbingan.push(b);
                            } else {
                              console.log(`Skipping duplicate in rekap: ${key}`);
                            }
                          });
                          
                          console.log(`After deduplication: ${dedupedBimbingan.length}`);
                          
                          // Now count only reviewed sessions
                          const countedSessions = dedupedBimbingan.filter(b => b.status === 'reviewed');
                          const count = countedSessions.length;
                          
                          console.log(`Found ${count} reviewed sessions (after deduplication):`);
                          countedSessions.forEach((s, i) => {
                            console.log(`Session ${i+1}: id=${s.id}, jenis=${s.jenisBimbingan}, sesi=${s.sesi}`);
                          });
                          
                          // Also track pending sessions
                          const pendingSessions = dedupedBimbingan.filter(b => b.status === 'pending');
                          const pendingCount = pendingSessions.length;
                          
                          console.log(`Found ${pendingCount} pending sessions:`);
                          pendingSessions.forEach((s, i) => {
                            console.log(`Pending ${i+1}: id=${s.id}, jenis=${s.jenisBimbingan}, sesi=${s.sesi}`);
                          });
                          
                          return (
                            <div key={p.id} className="col-md-6 mb-2">
                              <Card className="h-100 shadow-sm">
                                <Card.Body>
                                  <h6><strong>{`P${idx+1}: ${p.nama}`}</strong></h6>
                                  <div className="d-flex align-items-center mt-2">
                                    <div className="me-3">
                                      <Badge bg="primary" pill style={{ fontSize: "1.1rem", padding: "8px 12px" }}>
                                        {count}
                                      </Badge>
                                    </div>
                                    <div>
                                      <span className="text-muted">Sesi bimbingan selesai</span>
                                      {pendingCount > 0 && (
                                        <div className="mt-1">
                                          <Badge bg="warning">{pendingCount} Menunggu review</Badge>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </Card.Body>
                              </Card>
                            </div>
                          );
                        })}
                      </div>
                    </Card.Body>
                  </Card>
                  <Alert variant="info">
                    <strong>Judul Tugas Akhir:</strong> {tugasAkhir.judul}
                  </Alert>
                </div>
              )}
              {renderBimbinganTable()}
              {/* Only show Add Modal for Mahasiswa */}
              {userRole === 'mahasiswa' && renderAddBimbinganModal()}
              {renderDetailModal()}
            </Card.Body>
          </Card>
        </Container>
      )}
      <EditBimbinganModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        bimbingan={selectedEditBimbingan}
        onSave={handleSaveEdit}
        loading={loadingEdit}
        error={errorEdit}
        isReview={selectedEditBimbingan?.status === 'pending'}
      />
    </>
  );
};

export default Bimbingan;
